import { useEffect, useState } from 'react';
import { fetchApi, buildLinkUrl, subscribeToMasterBallotUpdates } from './utils';
import { useParams, Link } from 'react-router-dom';
import './TableData.css';

const USER_DATA_REQUEST = 'userGuesses';
const CATEGORY_DATA_REQUEST = "categoryGuesses";

type UserDataEntry = {
  category_name: string,
  category: string,
  points: number,
  winners: string[],
  entry?: string,
  consensus: string[],
}

type CategoryDataEntry = {
  user: string,
  guess: string,
}

function Header(props: { title: string }) {
  return <div className="header">
    <h3>{props.title}</h3>
    <Link to={buildLinkUrl('/standings')}>
      <button className="close" aria-label="Close"></button>
      </Link>
  </div>
}

function UserTable(props: any) {
  let params = useParams();

  const [state, setState] = useState<{ username: string, categories: UserDataEntry[] }>({
    username: '',
    categories: [],
  });

  const fetchData = () => {
    fetchApi(USER_DATA_REQUEST + '/' + params.user).then((responseJson) => {
      // Normalise: old backend returns winner (string), new backend returns
      // winners (string[]). Ensure winners is always a string[].
      responseJson.categories = responseJson.categories?.map((cat: any) => ({
        ...cat,
        winners: cat.winners ?? (cat.winner ? [cat.winner] : []),
      }));
      setState(responseJson);
    });
  };

  useEffect(() => {
    document.title = 'Awards Site : Entries';
    fetchData();

    const unsubscribe = subscribeToMasterBallotUpdates((data) => {
      // Refetch on master ballot update
      fetchData();
    });

    return () => unsubscribe();
  }, [params.user]);

  return <div className="results">
    <Header title={state.username} />
    <table className="resulttable">
      <thead>
        <tr>
          <th>Category</th>
          <th>{state.username}'s Guess</th>
          <th>Actual Winner<br />
            <span className="consensus">or group consensus</span>
          </th>
          <th>Points</th>
        </tr>
      </thead>
      <tbody>
        {state.categories.map((entry) => {
          const hasWinner = entry.winners.length > 0;
          const guess = (entry.entry ? true : false);
          const correct = entry.entry !== undefined && entry.winners.includes(entry.entry);
          const url = '/table/category/' + entry.category;
          return <tr data-guess={guess} data-winner={hasWinner}
            data-correct={correct} key={entry.category}>
            <td data-label='Category'>
              <Link
                to={buildLinkUrl(url)}
              >{entry.category_name}</Link>
            </td>
            <td data-label='Guess'>{entry.entry}</td>
            <td data-label='Winner' className={hasWinner ? '' : 'consensus'}>
              {hasWinner ? `${entry.winners.length > 1 ? 'Tie: ' : ''}${entry.winners.join(' & ')}` : entry.consensus.join('; ')}
            </td>
            <td data-label='Points'>{correct ? entry.points : '-'}</td>
          </tr>
        })}
      </tbody>
    </table>
  </div>
}

function CategoryTable(props: any) {
  let params = useParams();

  const [state, setState] = useState<{ category: string, category_name: string, winners: string[], consensus: string[], users: CategoryDataEntry[] }>({
    category: 'cat01',
    category_name: 'Temp Name',
    winners: [],
    consensus: ['Value'],
    users: [{ user: 'Person1', guess: 'Value' }],
  });

  const fetchData = () => {
    fetchApi(CATEGORY_DATA_REQUEST + '/' + params.category).then((responseJson) => {
      console.log(responseJson);
      // Normalise: old backend returns winner (string), new backend returns
      // winners (string[]). Ensure winners is always a string[].
      responseJson.winners = responseJson.winners ?? (responseJson.winner ? [responseJson.winner] : []);
      setState(responseJson);
    });
  };

  useEffect(() => {
    fetchData();

    const unsubscribe = subscribeToMasterBallotUpdates((data) => {
      // Refetch on master ballot update
      fetchData();
    });

    return () => unsubscribe();
  }, [params.category]);

  const hasWinner = state.winners.length > 0;

  return <div className="results">
    <Header title={state.category_name} />
    <div className="category_status">
      {hasWinner ? (
        <div className="winner">{state.winners.length > 1 ? 'Tie: ' : ''}{state.winners.join(' & ')}</div>
      ) : (
        <div className="consensus">{state.consensus.join('; ')}</div>
      )}
    </div>
    <table className="resulttable">
      <thead>
        <tr>
          <th>Name</th>
          <th>Guess</th>
        </tr>
      </thead>
      <tbody>
        {state.users.map((user) => {
          const guess = (user.guess ? true : false);
          const correct = hasWinner && state.winners.includes(user.guess);
          const url = '/table/user/' + user.user;
          return <tr data-guess={guess} data-correct={correct} key={user.user}>
            <td data-label='User'>
              <Link
                to={buildLinkUrl(url)}>{user.user}</Link>
            </td>
            <td data-label='Guess'>{user.guess}</td>
          </tr>
        })}
      </tbody>
    </table>
  </div>
}
export { UserTable, CategoryTable };
